"use client";

import * as React from "react";
import { format, parse, isValid, startOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import type { DateRange } from "react-day-picker";
import { CalendarIcon } from "lucide-react";
import {
  Calendar,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Input,
  cn,
} from "visuais";

type Mode = "single" | "range";

type DatePickerGenericProps = {
  /** single → seleciona 1 data | range → seleciona intervalo */
  mode?: Mode;

  /** Valor atual. Se mode=single, é Date ou undefined; se mode=range, é { from?: Date; to?: Date } ou undefined */
  value?: Date | DateRange;

  /** Callback quando o usuário seleciona datas */
  onChange?: (val: Date | DateRange | undefined) => void;

  /** Placeholder quando não há data selecionada */
  placeholder?: string;

  /** Se quiser multiple months lado a lado (ex.: 1, 2) */
  numberOfMonths?: number;

  /** Se true, datas anteriores a hoje ficam desabilitadas */
  disableBeforeToday?: boolean;
  /** Regra customizada para desabilitar datas específicas */
  isDateDisabled?: (date: Date) => boolean;

  /** Se true, permite digitar a data diretamente no input */
  allowTyping?: boolean;

  disabled?: boolean;
  className?: string;

  /** Classe CSS para o botão trigger (permite controlar tamanho da fonte, etc.) */
  triggerClassName?: string;

  /** Trigger customizado (ex: TriggerCard) */
  trigger?: React.ReactNode;
};

/**
 * Aplica máscara de data no formato dd/mm/aaaa
 */
function applyDateMask(value: string): string {
  // Remove tudo que não é número
  const numbers = value.replace(/\D/g, "");

  // Aplica a máscara
  if (numbers.length <= 2) {
    return numbers;
  } else if (numbers.length <= 4) {
    return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
  } else {
    return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(
      4,
      8
    )}`;
  }
}

/**
 * Normaliza uma data removendo a hora e garantindo que seja válida
 */
function normalizeDate(date: Date | undefined): Date | undefined {
  if (!date || !isValid(date)) return undefined;
  return startOfDay(date);
}

/**
 * Tenta fazer parse de uma string de data em diferentes formatos
 */
function parseDateString(dateString: string): Date | null {
  if (!dateString.trim()) return null;

  // Remove espaços extras
  const cleanString = dateString.trim();

  // Tenta diferentes formatos de data
  const formats = [
    "dd/MM/yyyy",
    "dd-MM-yyyy",
    "dd.MM.yyyy",
    "dd/MM/yy",
    "dd-MM-yy",
    "dd.MM.yy",
  ];

  for (const formatStr of formats) {
    try {
      const parsed = parse(cleanString, formatStr, new Date());
      if (isValid(parsed)) {
        return parsed;
      }
    } catch {
      continue;
    }
  }

  return null;
}

/**
 * Formata uma data para exibição no formato dd/MM/yyyy
 */
function formatDateForDisplay(date: Date): string {
  try {
    return format(date, "dd/MM/yyyy", { locale: ptBR });
  } catch {
    return "";
  }
}

/**
 * Componente unificado que aceita `mode="single"` ou `"range"`.
 * Exibe data única ou intervalo de datas, sempre com pt-BR.
 * Suporta digitação direta quando allowTyping=true.
 * Suporta trigger customizado quando trigger é fornecido.
 *
 * @example
 * ```tsx
 * // Trigger padrão
 * <DatePicker
 *   value={date}
 *   onChange={setDate}
 *   placeholder="Selecione uma data"
 * />
 *
 * // Trigger customizado
 * <DatePicker
 *   value={date}
 *   onChange={setDate}
 *   trigger={<TriggerCard icon={<CalendarIcon />} label="Data" isValid={!!date} />}
 * />
 * ```
 */
export function DatePicker({
  mode = "single",
  value,
  onChange,
  placeholder,
  numberOfMonths = mode === "range" ? 2 : 1,
  disableBeforeToday = false,
  isDateDisabled,
  allowTyping = false,
  disabled = false,
  className,
  triggerClassName,
  trigger,
}: DatePickerGenericProps) {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");
  const [isTyping, setIsTyping] = React.useState(false);
  const [triggerWidth, setTriggerWidth] = React.useState<string>("auto");
  const [calendarMinWidth, setCalendarMinWidth] = React.useState<number>(320);
  const customTriggerRef = React.useRef<HTMLDivElement>(null);
  const calendarRef = React.useRef<HTMLDivElement>(null);

  let selected: Date | DateRange | undefined;
  if (mode === "single") {
    selected = normalizeDate(value as Date);
  } else {
    const range = value as DateRange;
    selected = {
      from: normalizeDate(range?.from),
      to: normalizeDate(range?.to),
    };
  }

  // Atualiza o input quando o valor muda (apenas se não estiver digitando)
  React.useEffect(() => {
    if (isTyping) return;

    if (mode === "single" && selected instanceof Date) {
      setInputValue(formatDateForDisplay(selected));
    } else if (mode === "range" && typeof selected === "object") {
      const range = selected as DateRange;
      if (range.from) {
        setInputValue(
          range.to
            ? `${formatDateForDisplay(range.from)} - ${formatDateForDisplay(
                range.to
              )}`
            : formatDateForDisplay(range.from)
        );
      } else {
        setInputValue("");
      }
    } else {
      setInputValue("");
    }
  }, [selected, mode, isTyping]);

  let buttonLabel =
    placeholder ||
    (mode === "range" ? "Selecione intervalo" : "Selecione data");

  if (mode === "single" && selected instanceof Date) {
    buttonLabel = formatDateForDisplay(selected);
  } else if (mode === "range" && typeof selected === "object") {
    const range = selected as DateRange;
    if (range.from) {
      buttonLabel = range.to
        ? `${formatDateForDisplay(range.from)} - ${formatDateForDisplay(
            range.to
          )}`
        : formatDateForDisplay(range.from);
    }
  }

  const handleSelect = (sel: Date | DateRange | undefined) => {
    if (!disabled) {
      // Normaliza as datas antes de chamar onChange
      if (mode === "single") {
        onChange?.(normalizeDate(sel as Date));
      } else {
        const range = sel as DateRange;
        onChange?.({
          from: normalizeDate(range?.from),
          to: normalizeDate(range?.to),
        });
      }
    }
  };
  const isBlockedDate = React.useCallback(
    (date: Date) => {
      const normalized = startOfDay(date);
      if (disableBeforeToday && normalized < startOfDay(new Date())) {
        return true;
      }
      if (isDateDisabled?.(normalized)) {
        return true;
      }
      return false;
    },
    [disableBeforeToday, isDateDisabled]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setIsTyping(true);

    // Aplica máscara apenas se o usuário estiver digitando
    const maskedValue = applyDateMask(value);
    setInputValue(maskedValue);
  };

  const handleInputBlur = () => {
    setIsTyping(false);

    // Se o input estiver vazio, limpa a data
    if (!inputValue.trim()) {
      onChange?.(undefined);
      return;
    }

    // Tenta fazer parse da data digitada
    if (mode === "single") {
      const parsedDate = parseDateString(inputValue);

      if (parsedDate && isValid(parsedDate)) {
        const normalizedDate = normalizeDate(parsedDate);
        if (!normalizedDate) {
          setInputValue("");
          onChange?.(undefined);
          return;
        }
        if (isBlockedDate(normalizedDate)) {
          if (selected instanceof Date) {
            setInputValue(formatDateForDisplay(selected));
          } else {
            setInputValue("");
          }
          return;
        }
        onChange?.(normalizedDate);
        setInputValue(formatDateForDisplay(normalizedDate!));
      } else {
        // Se não conseguir fazer parse, limpa o input
        setInputValue("");
        onChange?.(undefined);
      }
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    }
  };

  const handleInputFocus = () => {
    setIsTyping(true);
  };

  const updateWidth = React.useCallback(() => {
    if (customTriggerRef.current) {
      const rect = customTriggerRef.current.getBoundingClientRect();
      setTriggerWidth(`${rect.width}px`);
    }
  }, []);

  const updateCalendarMinWidth = React.useCallback(() => {
    if (calendarRef.current) {
      // Largura mínima baseada no número de meses
      const baseWidth = 320; // Largura mínima para 1 mês
      const additionalWidth = (numberOfMonths - 1) * 280; // ~280px por mês adicional
      const calculatedMinWidth = baseWidth + additionalWidth;

      // Verifica a largura real do calendar se já estiver renderizado
      const calendarRect = calendarRef.current.getBoundingClientRect();
      const actualWidth = calendarRect.width;

      // Usa o maior entre o calculado e o real (se disponível)
      setCalendarMinWidth(
        Math.max(calculatedMinWidth, actualWidth || calculatedMinWidth)
      );
    }
  }, [numberOfMonths]);

  React.useEffect(() => {
    if (open && trigger) {
      updateWidth();
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }
  }, [open, trigger, updateWidth]);

  React.useEffect(() => {
    if (open) {
      // Aguarda um tick para o calendar ser renderizado
      const timer = setTimeout(() => {
        updateCalendarMinWidth();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [open, updateCalendarMinWidth]);

  const triggerButton = React.useMemo(() => {
    if (trigger) {
      return trigger;
    }

    return (
      <Button
        variant="outline"
        disabled={disabled}
        className={cn(
          "w-full justify-start text-left font-normal",
          !value && "text-muted-foreground",
          disabled && "opacity-60 cursor-not-allowed",
          triggerClassName
        )}
      >
        <CalendarIcon className="mr-2 h-4 w-4" />
        {buttonLabel}
      </Button>
    );
  }, [trigger, buttonLabel, disabled, value, triggerClassName]);

  if (allowTyping && mode === "single" && !trigger) {
    return (
      <div className={cn("grid gap-2", className)}>
        <div className="relative">
          <Input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            onKeyDown={handleInputKeyDown}
            placeholder={placeholder || "dd/mm/aaaa"}
            disabled={disabled}
            className={cn("pr-10", disabled && "opacity-60 cursor-not-allowed")}
          />
          <Popover
            open={open}
            onOpenChange={(open) => !disabled && setOpen(open)}
          >
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                className={cn(
                  "absolute right-0 top-0 h-full px-3",
                  disabled && "opacity-60 cursor-not-allowed",
                  triggerClassName
                )}
              >
                <CalendarIcon className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="center">
              <div ref={calendarRef} className="flex justify-center">
                <Calendar
                  locale={ptBR}
                  mode="single"
                  numberOfMonths={numberOfMonths}
                  selected={selected as Date}
                  onSelect={(date) => {
                    handleSelect(date);
                    setOpen(false);
                  }}
                  disabled={isBlockedDate}
                />
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    );
  }

  // Comportamento original para range ou quando allowTyping=false
  return (
    <div className={cn("grid gap-2", className)}>
      <Popover open={open} onOpenChange={(open) => !disabled && setOpen(open)}>
        <PopoverTrigger asChild>
          {trigger ? (
            <div ref={customTriggerRef} className="h-full">
              {triggerButton}
            </div>
          ) : (
            triggerButton
          )}
        </PopoverTrigger>

        {!disabled && (
          <PopoverContent
            className="w-auto p-0"
            align="center"
            style={{
              width: trigger ? triggerWidth : "auto",
              minWidth: `${calendarMinWidth}px`,
            }}
          >
            <div ref={calendarRef} className="flex justify-center">
              {mode === "single" ? (
                <Calendar
                  locale={ptBR}
                  mode="single"
                  numberOfMonths={numberOfMonths}
                  selected={selected as Date}
                  onSelect={(date) => handleSelect(date)}
                  disabled={isBlockedDate}
                />
              ) : (
                <Calendar
                  locale={ptBR}
                  mode="range"
                  numberOfMonths={numberOfMonths}
                  selected={selected as DateRange}
                  onSelect={(range) => handleSelect(range)}
                  defaultMonth={
                    typeof selected === "object" && selected
                      ? (selected as DateRange).from || undefined
                      : undefined
                  }
                  disabled={isBlockedDate}
                />
              )}
            </div>
          </PopoverContent>
        )}
      </Popover>
    </div>
  );
}
